import {
  Directive,
  ElementRef,
  Input,
  OnInit,
  OnDestroy,
  OnChanges,
  SimpleChanges,
  Renderer2,
  Inject,
  PLATFORM_ID,
  NgZone
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { MobileOptimizationService } from '../services/mobile-optimization.service';

@Directive({
  selector: '[appMobileOptimization]',
  standalone: true
})
export class MobileOptimizationDirective implements OnInit, OnDestroy, OnChanges {
  @Input() originalUrl: string = '';
  @Input() width?: number | string;
  @Input() height?: number | string;
  @Input() quality?: number | string;
  @Input() format?: string;
  @Input() priority?: boolean = false;
  @Input() isPoster?: boolean = false; // New input for video poster optimization
  @Input() fit?: 'contain' | 'cover' | 'inside';
  @Input() backgroundColor?: string; // Optional background color (hex), default none

  private _origWidth?: number;
  private _origHeight?: number;
  private _initialized = false;
  private _imageErrorCleanup?: () => void;
  private _imageLoadCleanup?: () => void;
  private _loadTimeoutId?: ReturnType<typeof setTimeout>;
  private static readonly LOAD_FALLBACK_MS = 15000;

  constructor(
    private el: ElementRef,
    private renderer: Renderer2,
    @Inject(PLATFORM_ID) private platformId: Object,
    private mobileOptimizationService: MobileOptimizationService,
    private ngZone: NgZone
  ) {}

  ngOnInit(): void {
    // Read numeric inputs into local originals (coerce)
    this._origWidth = this.coerceNumber(this.width);
    this._origHeight = this.coerceNumber(this.height);
    
    // Mark as initialized and update image outside Angular to avoid unnecessary CD
    this._initialized = true;
    this.ngZone.runOutsideAngular(() => this.updateImage());
  }

  ngOnChanges(changes: SimpleChanges): void {
    // If not initialized yet, skip - ngOnInit will handle it
    if (!this._initialized) {
      return;
    }
    
    // If width/height changed after init, update stored originals
    if (changes['width']) {
      this._origWidth = this.coerceNumber(this.width);
    }
    if (changes['height']) {
      this._origHeight = this.coerceNumber(this.height);
    }
    
    // Only update if originalUrl changed or dimensions changed
    if (changes['originalUrl'] || changes['width'] || changes['height'] || changes['quality'] || changes['format']) {
      this.ngZone.runOutsideAngular(() => this.updateImage());
    }
  }

  ngOnDestroy(): void {
    this.clearFallbackListeners();
  }

  private clearFallbackListeners(): void {
    if (this._loadTimeoutId) {
      clearTimeout(this._loadTimeoutId);
      this._loadTimeoutId = undefined;
    }
    if (this._imageErrorCleanup) {
      this._imageErrorCleanup();
      this._imageErrorCleanup = undefined;
    }
    if (this._imageLoadCleanup) {
      this._imageLoadCleanup();
      this._imageLoadCleanup = undefined;
    }
  }

  private updateImage(): void {
    this.clearFallbackListeners();

    const native = this.el.nativeElement;
    if (!native) return; // Handle cases where native element is null or undefined

    // Determine if the element is an image or a video poster
    const isImage = native.tagName === 'IMG';
    const isVideoPoster = native.tagName === 'VIDEO' && this.isPoster; // Use isPoster flag instead of checking for existing attribute

    if (!isImage && !isVideoPoster) return; // Only optimize images and video posters

    const img = isImage ? native as HTMLImageElement : null;
    const videoPoster = isVideoPoster ? native as HTMLVideoElement : null;

    // If no originalUrl passed, use existing src as fallback
    const sourceUrl = this.originalUrl || (isImage ? img?.getAttribute('src') : videoPoster?.getAttribute('poster')) || '';
    if (!sourceUrl) return;

    // Ensure sourceUrl is a string before calling string methods
    const sourceUrlString = String(sourceUrl);
    if (!sourceUrlString) return;

    // Skip optimization for SVG files as they are vector graphics
    if (sourceUrlString.toLowerCase().endsWith('.svg')) return;

    // Compute responsive dimensions without overwriting @Input
    const { width: reqW, height: reqH } = this.computeRequestedDimensions();

    // Don't request size greater than original known dimensions (avoid upscaling)
    const widthToRequest = this._origWidth ? Math.min(reqW ?? this._origWidth, this._origWidth) : reqW;
    const heightToRequest = this._origHeight ? Math.min(reqH ?? this._origHeight, this._origHeight) : reqH;

    // Determine format: use 'gif' if originalUrl ends with .gif, otherwise use passed format
    const formatToUse = sourceUrlString.toLowerCase().endsWith('.gif') ? 'gif' : this.format;

    // Build optimized URL (service will handle quality if undefined)
    const qualityNum = this.coerceNumber(this.quality);
    const optimized = this.isPoster
      ? this.mobileOptimizationService.getOptimizedPosterUrl(sourceUrlString, widthToRequest, heightToRequest, this.fit, this.backgroundColor)
      : this.mobileOptimizationService.getOptimizedImageUrl(
          sourceUrlString,
          widthToRequest,
          heightToRequest,
          qualityNum !== undefined ? qualityNum : undefined,
          formatToUse,
          this.fit,
          this.backgroundColor
        );

    // Build srcset for responsive images - only add 2x if dimensions are small enough
    let srcset = optimized;
    try {
      // Only generate 2x variant for high-DPI if:
      // 1. Original width is known AND
      // 2. Original is at least 2x the requested size AND
      // 3. Requested size is reasonable (avoid excessive upscaling)
      const shouldGenerate2x = this._origWidth && 
        this._origWidth >= (widthToRequest ?? 0) * 2 &&
        (widthToRequest ?? 0) < 1000; // Arbitrary limit to avoid huge requests
      
      if (shouldGenerate2x && !this.isPoster) {
        // Only generate 2x for regular images, not for posters
        const optimized2x = this.mobileOptimizationService.getOptimizedImageUrl(
          sourceUrlString,
          widthToRequest ? Math.round((widthToRequest ?? 0) * 2) : undefined,
          heightToRequest ? Math.round((heightToRequest ?? 0) * 2) : undefined,
          qualityNum !== undefined ? qualityNum : undefined,
          formatToUse,
          this.fit,
          this.backgroundColor
        );
        srcset = `${optimized} 1x, ${optimized2x} 2x`;
      } else {
        srcset = `${optimized} 1x`;
      }
    } catch (e) {
      // fallback to single src
      srcset = optimized;
    }

    // For SSR, use direct DOM manipulation; for browser, use Renderer2
    if (isPlatformBrowser(this.platformId)) {
      // Browser: Use Renderer2 for better Angular integration
      if (isImage && img) {
        this.renderer.setAttribute(img, 'src', optimized);
        this.renderer.setAttribute(img, 'srcset', srcset);
        this.setupImageFallback(img, optimized, sourceUrlString);
      } else if (isVideoPoster && videoPoster) {
        this.renderer.setAttribute(videoPoster, 'poster', optimized);
        this.setupPosterFallback(videoPoster, optimized, sourceUrlString);
      }
      
      if (isImage && img && !img.hasAttribute('loading')) {
        this.renderer.setAttribute(img, 'loading', this.priority ? 'eager' : 'lazy');
      }
      
      if (isImage && img && this.priority) {
        this.renderer.setAttribute(img, 'fetchpriority', 'high');
      } else if (isImage && img) {
        this.renderer.removeAttribute(img, 'fetchpriority');
      }
    } else {
      // SSR: Use direct DOM manipulation (faster for server-side rendering)
      if (isImage && img) {
        img.setAttribute('src', optimized);
        img.setAttribute('srcset', srcset);
      } else if (isVideoPoster && videoPoster) {
        videoPoster.setAttribute('poster', optimized);
      }
      
      if (isImage && img && !img.hasAttribute('loading')) {
        img.setAttribute('loading', this.priority ? 'eager' : 'lazy');
      }
      
      if (isImage && img && this.priority) {
        img.setAttribute('fetchpriority', 'high');
      }
    }

    // Set width/height attributes (use original values so layout doesn't shift)
    if (this._origWidth && isImage && img) {
      const width = String(this._origWidth);
      if (isPlatformBrowser(this.platformId)) {
        this.renderer.setAttribute(img, 'width', width);
      } else {
        img.setAttribute('width', width);
      }
    }
    
    if (this._origHeight && isImage && img) {
      const height = String(this._origHeight);
      if (isPlatformBrowser(this.platformId)) {
        this.renderer.setAttribute(img, 'height', height);
      } else {
        img.setAttribute('height', height);
      }
    }

    // Ensure alt exists
    if (isImage && img && !img.hasAttribute('alt')) {
      if (isPlatformBrowser(this.platformId)) {
        this.renderer.setAttribute(img, 'alt', 'TopFan');
      } else {
        img.setAttribute('alt', 'TopFan');
      }
    }
  }

  /**
   * Sets up error and load-timeout fallback for img: on error or timeout, use original URL.
   */
  private setupImageFallback(img: HTMLImageElement, optimizedUrl: string, originalUrl: string): void {
    const applyFallback = (): void => {
      if (this._loadTimeoutId) {
        clearTimeout(this._loadTimeoutId);
        this._loadTimeoutId = undefined;
      }
      if (this._imageErrorCleanup) {
        this._imageErrorCleanup();
        this._imageErrorCleanup = undefined;
      }
      if (this._imageLoadCleanup) {
        this._imageLoadCleanup();
        this._imageLoadCleanup = undefined;
      }
      this.renderer.setAttribute(img, 'src', originalUrl);
      this.renderer.removeAttribute(img, 'srcset');
    };

    this._imageErrorCleanup = this.renderer.listen(img, 'error', () => applyFallback());
    this._imageLoadCleanup = this.renderer.listen(img, 'load', () => {
      if (this._loadTimeoutId) {
        clearTimeout(this._loadTimeoutId);
        this._loadTimeoutId = undefined;
      }
    });

    this._loadTimeoutId = setTimeout(() => {
      if (img.getAttribute('src') === optimizedUrl && !img.complete) {
        applyFallback();
      }
      this._loadTimeoutId = undefined;
    }, MobileOptimizationDirective.LOAD_FALLBACK_MS);
  }

  /**
   * Tests optimized poster URL with an Image(); on error, sets video poster to original URL.
   */
  private setupPosterFallback(video: HTMLVideoElement, optimizedUrl: string, originalUrl: string): void {
    const testImg = new Image();
    testImg.onerror = () => {
      this.renderer.setAttribute(video, 'poster', originalUrl);
    };
    testImg.src = optimizedUrl;
  }

  private computeRequestedDimensions(): { width?: number; height?: number } {
    // If no original dims provided, try to use requested inputs; otherwise rely on service
    const origW = this._origWidth;
    const origH = this._origHeight;

    // Default to original if nothing provided
    let w = origW;
    let h = origH;

    // If both original are missing but width/height inputs given, coerce them
    if (!w && this.width) {
      w = this.coerceNumber(this.width);
    }
    if (!h && this.height) {
      h = this.coerceNumber(this.height);
    }

    // Responsive adjustments (no mutation of inputs)
    const aspect = (origW && origH) ? origW / origH : (w && h ? (w / h) : undefined);
    const breakpoint = this.mobileOptimizationService.getCurrentBreakpoint();

    switch (breakpoint) {
      case 'mobile':
        // For mobile, be more aggressive - limit to max 300px width while maintaining aspect ratio
        // This ensures we don't download unnecessarily large images for small displays
        if (aspect && (w ?? 0) > 300) {
          if ((w ?? 0) >= (h ?? 0)) {
            return { width: 300, height: Math.round(300 / aspect) };
          } else {
            const newH = 400;
            return { width: Math.round(newH * aspect), height: newH };
          }
        }
        // If dimensions are already reasonable for mobile, don't resize
        return { width: w, height: h };
      case 'tablet':
        // For tablet, limit to max 700px while maintaining aspect ratio
        if (aspect && (w ?? 0) > 700) {
          if ((w ?? 0) >= (h ?? 0)) {
            return { width: 700, height: Math.round(700 / aspect) };
          } else {
            const newH = 700;
            return { width: Math.round(newH * aspect), height: newH };
          }
        }
        // If dimensions are already reasonable for tablet, don't resize
        return { width: w, height: h };
      case 'desktop':
      default:
        // For desktop, use original dimensions (no resizing)
        return { width: w, height: h };
    }
  }

  private coerceNumber(val?: number | string): number | undefined {
    if (val === undefined || val === null) return undefined;
    if (typeof val === 'number') return val;
    const n = Number(val);
    return isNaN(n) ? undefined : n;
  }
}